import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  DialogRef,
  DialogService,
} from '@cyborg-monorepo/common/angular/cyborg-ui';
import { Enums, Profile } from '@cyborg-monorepo/hunter/common';
import { AuthService } from '../../../../auth/auth.service';
import { ReleaseService } from '../../../../changelog/release.service';
import { FeatureFlagService } from '../../../../helpers/feature-flag.service';
import { SearchStateService } from '../../../research/services/search-state.service';
import { SearchUrlService } from '../../../research/services/search-url.service';
import { CalendlyService } from '../../services/calendly.service';
import { UserService } from '../../services/user.service';
import { HuntModuleDialogComponent } from '../dialogs/hunt-module-dialog/hunt-module-dialog.component';
import { TalkToCyborgDialogComponent } from '../dialogs/talk-to-cyborg-dialog/talk-to-cyborg-dialog.component';
import {
  TrackingEventEnum,
  TrackingService,
} from '../../../../tracking/tracking.service';
import { FrameDetectionService } from '../../services/frame-detection.service';

import { IconComponent } from '@hyperkit'

/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
const getURLPath = (): string | null => {
  const pathname = window.location.pathname; // Get the path (e.g., "/hunt/dashboard")
  const firstPath = pathname.split('/').filter((segment) => segment)[0]; // Extract the first segment
  return firstPath || null;
};

@Component({
  standalone: false,
  selector: 'header',
  styleUrls: ['./header.component.scss'],
  templateUrl: './header.component.html',
})
export class HeaderComponent implements OnInit {
  private searchStateService = inject(SearchStateService);
  private searchUrlService = inject(SearchUrlService);
  private userService = inject(UserService);
  private authService = inject(AuthService);
  private releaseService = inject(ReleaseService);
  private dialogService = inject(DialogService);
  private featureFlagService = inject(FeatureFlagService);
  private calendlyService = inject(CalendlyService);
  private trackingService = inject(TrackingService);
  private frameDetectionService = inject(FrameDetectionService);


  private router = inject(Router);
  public firstName: string;
  public lastName: string;
  public role: string;
  public searchForm: FormGroup;
  public avatar = '/assets/brand/intel-bug-light.svg';
  public loggedIn: boolean;
  public latest: string;
  public daysRemaining: number = 30;
  public daysThreshold: number = 30;
  public status: Enums.CompanyStatus;
  public expires: Date;
  public statusTypes = Enums.CompanyStatus;
  public huntModuleAccess: boolean = false;
  public activeRoute = '';
  public isiFrame: boolean = false;

  constructor(
  ) {
    this.router.events.subscribe(() => {
      const fullPath = this.router.url; // Get the current route
      this.activeRoute = '/' + fullPath.split('/')[1]; // Extract the first segment (e.g., "/hunt")
    });
  }

  public isActive(link: string): boolean {
    return this.activeRoute === link;
  }

  public ngOnInit(): void {

    if (this.frameDetectionService.isIframe()) {
      console.log("is iframe");
      this.isiFrame = true; 
    }

    this.searchForm = new FormGroup({
      search: new FormControl('', [Validators.required]),
    });

    this.latest = this.releaseService.getLatestVersion();
    this.huntModuleAccess = this.featureFlagService.huntModuleAccess();

    if (this.authService.isAuthenticated()) {
      this.loggedIn = true;
      this.userService.getProfile().subscribe((profile: Profile) => {
        this.firstName = profile.firstName;
        this.lastName = profile.lastName;
        this.role = profile.role;
        this.status = profile.company.status;
        this.expires = new Date(profile.company.expires);

        const oneDay = 24 * 60 * 60 * 1000; // hours*minutes*seconds*milliseconds
        const today = new Date();

        this.daysRemaining = Math.round(
          Math.abs((this.expires.valueOf() - today.valueOf()) / oneDay),
        );
      });
    } else {
      this.loggedIn = false;
    }
  }

  public onLogOut(): void {
    this.authService.logout();
  }

  public openPremiumDialog(): void {
    this.trackingService.trackEvent(
      TrackingEventEnum.COMMUNITY_TALK_WITH_US_DIALOG_OPENED,
    );
    const dialogRef: DialogRef = this.dialogService.open(
      TalkToCyborgDialogComponent,
    );

    dialogRef.afterClosed().subscribe((talkToCyborg: boolean) => {
      if (talkToCyborg) {
        this.talkToCyborg();
      } else {
        this.trackingService.trackEvent(
          TrackingEventEnum.COMMUNITY_TALK_WITH_US_DIALOG_ABANDONED,
        );
      }
    });
  }

  public openHuntDialog(): void {
    this.trackingService.trackEvent(
      TrackingEventEnum.COMMUNITY_HUNT_MODULE_DIALOG_OPENED,
    );
    const dialogRef: DialogRef = this.dialogService.open(
      HuntModuleDialogComponent,
      {
        panelClass: 'full-panel',
      },
    );

    dialogRef.afterClosed().subscribe((talkToCyborg: boolean) => {
      if (talkToCyborg) {
        this.talkToCyborg();
      } else {
        this.trackingService.trackEvent(
          TrackingEventEnum.COMMUNITY_HUNT_MODULE_DIALOG_ABANDONED,
        );
      }
    });
  }

  public talkToCyborg(): void {
    this.calendlyService.talkToCyborg();
  }

  public onSearch(): void {
    const term: string = this.searchForm.value.search;
    if (term) {
      this.searchStateService.setTerm(term);
      this.searchUrlService.gotoUrl(this.searchStateService.state.value);
      this.searchForm.setValue({ search: '' });
    }
  }
}
